// Raw Supabase REST helpers. Bypasses supabase-js entirely so a wedged
// internal socket / mid-refresh promise can't strand a page on Loading.
//
// Live testing repeatedly catches supabase-js queries hanging for tens of
// seconds in long-lived browser sessions (after the page has been idle,
// after a token refresh, etc) while a direct fetch returns the same row
// in ~120ms. The pattern was first proven on the QR page (QrLabels.tsx);
// this module factors it out so every screen-level read can use it.
//
// Usage:
//   const rows = await restGet<Project[]>('projects', {
//     select: '*', order: 'name', timeoutMs: 8000,
//   })
//
// The Supabase access token is read from localStorage (the supabase-js
// auth-token blob). We never call supabase.auth.getSession() — that path
// is itself prone to wedging.
//
// Errors:
//   - Auth missing → throws 'Not signed in'.
//   - Non-2xx → throws 'HTTP <status>: <first 200 chars of body>'.
//   - Timeout → throws '<label> timed out (<ms>ms)' (via withTimeout).

import { withTimeout } from './withTimeout'

const URL_BASE = (import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '')
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string

function readToken(): string | null {
  try {
    const key = Object.keys(localStorage).find(k => k.includes('auth-token'))
    if (!key) return null
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.access_token || null
  } catch { return null }
}

function authHeaders(): Record<string, string> {
  const token = readToken()
  if (!token) throw new Error('Not signed in. Sign out and back in.')
  return {
    apikey: ANON,
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
}

export interface RestGetOptions {
  /** Comma-separated columns or embed syntax, e.g. '*' or 'id,name,project:projects(id,name)' */
  select?: string
  /** PostgREST filters as raw query string fragments, e.g. ['project_id=eq.123', 'status=neq.closed'] */
  filters?: string[]
  /** Order spec, e.g. 'created_at.desc' or 'created_at.asc.nullslast' */
  order?: string | string[]
  /** Row limit. */
  limit?: number
  /** If true, returns the FIRST row or null (maybeSingle semantics). */
  single?: boolean
  /** Hard timeout in ms (default 8000). */
  timeoutMs?: number
  /** Label for the timeout error message. */
  label?: string
  /** If true, use HEAD and return the row count from the Content-Range header instead of rows. */
  countOnly?: boolean
}

export async function restGet<T = any>(table: string, opts: RestGetOptions = {}): Promise<T> {
  const params = new URLSearchParams()
  params.set('select', opts.select || '*')
  if (opts.filters) for (const f of opts.filters) {
    const eq = f.indexOf('=')
    if (eq > 0) params.append(f.slice(0, eq), f.slice(eq + 1))
  }
  if (opts.order) {
    const orders = Array.isArray(opts.order) ? opts.order : [opts.order]
    for (const o of orders) params.append('order', o)
  }
  if (opts.limit) params.set('limit', String(opts.limit))

  const headers = authHeaders()
  if (opts.countOnly) headers['Prefer'] = 'count=exact'
  if (opts.single) headers['Accept'] = 'application/vnd.pgrst.object+json'

  const method = opts.countOnly ? 'HEAD' : 'GET'
  const url = `${URL_BASE}/rest/v1/${table}?${params.toString()}`
  const label = opts.label || `${method} ${table}`
  const timeoutMs = opts.timeoutMs ?? 8000

  const res = await withTimeout(fetch(url, { method, headers, cache: 'no-store' }), timeoutMs, label)
  if (!res.ok) {
    // 406 with single:true means "no row" — that's `null`, not an error.
    if (opts.single && res.status === 406) return null as unknown as T
    const text = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`)
  }

  if (opts.countOnly) {
    // Content-Range: "0-9/247" — the bit after the slash is the count.
    const cr = res.headers.get('Content-Range') || ''
    const n = parseInt(cr.split('/')[1] || '0', 10)
    return (Number.isFinite(n) ? n : 0) as unknown as T
  }

  // maybeSingle: PostgREST 200 + body is the object directly.
  if (opts.single) {
    const txt = await res.text()
    if (!txt) return null as unknown as T
    return JSON.parse(txt) as T
  }

  return await res.json() as T
}
